import { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, Trash2, BarChart2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import api from "../../lib/api";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(n || 0);
const fmtEURCents = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR",
}).format(n || 0);

const MONTH_NAMES_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const WEEKDAYS_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function startOfMonthFromRange(range) {
  // Prefer the `from` of the current filter so the calendar aligns with
  // the period the user is already looking at.
  if (range?.from) {
    const [y, m] = range.from.split("-").map(Number);
    if (y && m) return new Date(y, m - 1, 1);
  }
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/**
 * Monthly expense heatmap calendar used inside the "Gastos" tab.
 * - Receives pre-loaded rows grouped by ISO date (Map<"YYYY-MM-DD", row[]>)
 * - Lets the user navigate month-by-month inside the current filter range
 * - Clicking a day expands an inline details panel below
 */
export default function ExpensesCalendar({ byDate, range, catLabels, onDelete, restaurantId }) {
  const [cursor, setCursor] = useState(() => startOfMonthFromRange(range));
  const [selected, setSelected] = useState(null);    // "YYYY-MM-DD"
  // Category filter — null means "show all"; otherwise Set of active keys.
  const [activeCategories, setActiveCategories] = useState(null);
  const [activeSupplier, setActiveSupplier] = useState(null);  // Top-5 drilldown
  // Month-over-month comparison
  const [compareMode, setCompareMode] = useState(false);
  const [prevMonthRows, setPrevMonthRows] = useState([]);
  const [loadingPrev, setLoadingPrev] = useState(false);

  const year = cursor.getFullYear();
  const month = cursor.getMonth(); // 0-based

  // Compute first visible Monday and last visible Sunday of the grid
  const firstOfMonth = new Date(year, month, 1);
  const gridStart = new Date(firstOfMonth);
  const jsDow = (firstOfMonth.getDay() + 6) % 7; // 0 = Monday
  gridStart.setDate(firstOfMonth.getDate() - jsDow);

  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridStart.getTime()]);

  const toIso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  // Filtered byDate map — hides rows whose category isn't in activeCategories.
  const catFilteredByDate = useMemo(() => {
    if (!activeCategories || activeCategories.size === 0) return byDate;
    const out = new Map();
    for (const [iso, rows] of byDate.entries()) {
      const kept = rows.filter(r => activeCategories.has((r.category || "other").toLowerCase()));
      if (kept.length) out.set(iso, kept);
    }
    return out;
  }, [byDate, activeCategories]);

  // Further filter by supplier (click on a Top-5 bar)
  const filteredByDate = useMemo(() => {
    if (!activeSupplier) return catFilteredByDate;
    const out = new Map();
    for (const [iso, rows] of catFilteredByDate.entries()) {
      const kept = rows.filter(r => (r.supplier_name || "").trim() === activeSupplier);
      if (kept.length) out.set(iso, kept);
    }
    return out;
  }, [catFilteredByDate, activeSupplier]);

  // Categories present in the current month (for the legend chips).
  const categoriesInMonth = useMemo(() => {
    const m = new Map();
    for (const c of cells) {
      if (c.getMonth() !== month) continue;
      const rows = byDate.get(toIso(c)) || [];
      for (const r of rows) {
        const k = (r.category || "other").toLowerCase();
        const cur = m.get(k) || { count: 0, sum: 0 };
        cur.count += 1;
        cur.sum += Number(r.amount) || 0;
        m.set(k, cur);
      }
    }
    return [...m.entries()]
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => b.sum - a.sum);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells, byDate, month]);

  // Previous-month fetch whenever comparison mode is on or the cursor moves.
  useEffect(() => {
    let cancelled = false;
    if (!compareMode || !restaurantId) {
      setPrevMonthRows([]);
      return;
    }
    const prev = new Date(year, month - 1, 1);
    const prevLast = new Date(year, month, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const from = `${prev.getFullYear()}-${pad(prev.getMonth() + 1)}-01`;
    const to = `${prevLast.getFullYear()}-${pad(prevLast.getMonth() + 1)}-${pad(prevLast.getDate())}`;
    setLoadingPrev(true);
    (async () => {
      try {
        const rows = await api.listExpenses(restaurantId, { from, to });
        if (!cancelled) setPrevMonthRows(rows || []);
      } catch {
        if (!cancelled) setPrevMonthRows([]);
      } finally {
        if (!cancelled) setLoadingPrev(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, restaurantId, year, month]);

  const prevMonthTotal = useMemo(() => {
    if (!compareMode) return 0;
    const cats = activeCategories;
    return prevMonthRows.reduce((s, r) => {
      if (cats && cats.size > 0 && !cats.has((r.category || "other").toLowerCase())) return s;
      return s + (Number(r.amount) || 0);
    }, 0);
  }, [compareMode, prevMonthRows, activeCategories]);

  // Compute per-day total for cells in the current month only
  const monthStats = useMemo(() => {
    let max = 0;
    let total = 0;
    let days = 0;
    for (const c of cells) {
      if (c.getMonth() !== month) continue;
      const rows = filteredByDate.get(toIso(c)) || [];
      if (rows.length === 0) continue;
      const sum = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      if (sum > max) max = sum;
      total += sum;
      days += 1;
    }
    return { max, total, days };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells, filteredByDate, month]);

  const shadeFor = (amount) => {
    if (!amount || !monthStats.max) return "bg-card";
    const ratio = Math.min(1, amount / monthStats.max);
    // 6 buckets of violet intensity
    if (ratio >= 0.85) return "bg-violet-600 text-white";
    if (ratio >= 0.65) return "bg-violet-500 text-white";
    if (ratio >= 0.45) return "bg-violet-400 text-white";
    if (ratio >= 0.25) return "bg-violet-300 text-violet-900";
    if (ratio > 0) return "bg-violet-100 text-violet-900";
    return "bg-card";
  };

  // Fixed category → accent color for the tiny dot on each chip.
  const CATEGORY_DOT = {
    purchases: "bg-emerald-500",
    utilities: "bg-amber-500",
    telecommunications: "bg-sky-500",
    rent: "bg-rose-500",
    payroll: "bg-indigo-500",
    taxes: "bg-red-500",
    marketing: "bg-pink-500",
    maintenance: "bg-orange-500",
    cleaning: "bg-cyan-500",
    software: "bg-violet-500",
    delivery_apps: "bg-fuchsia-500",
    bank_fees: "bg-slate-500",
    professional: "bg-teal-500",
    insurance: "bg-blue-500",
    other: "bg-gray-400",
  };

  const fmtChipAmount = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}k€`;
    return `${Math.round(v)}€`;
  };

  // Top-5 suppliers of the visible month (respects category filter).
  const topSuppliers = useMemo(() => {
    const map = new Map();
    for (const c of cells) {
      if (c.getMonth() !== month) continue;
      const rows = catFilteredByDate.get(toIso(c)) || [];
      for (const r of rows) {
        const name = (r.supplier_name || "").trim();
        if (!name) continue;
        const cur = map.get(name) || { count: 0, sum: 0, category: r.category };
        cur.count += 1;
        cur.sum += Number(r.amount) || 0;
        map.set(name, cur);
      }
    }
    const arr = [...map.entries()].map(([name, v]) => ({ name, ...v }));
    arr.sort((a, b) => b.sum - a.sum);
    return arr.slice(0, 5);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells, catFilteredByDate, month]);

  const topMax = topSuppliers[0]?.sum || 1;

  const go = (deltaMonths) => {
    const d = new Date(year, month + deltaMonths, 1);
    setCursor(d);
    setSelected(null);
    setActiveSupplier(null);  // supplier filter is month-scoped
  };

  const selectedRows = selected ? (filteredByDate.get(selected) || []) : [];
  const selectedTotal = selectedRows.reduce(
    (s, r) => s + (Number(r.amount) || 0), 0,
  );

  // Month-over-month delta
  const delta = useMemo(() => {
    if (!compareMode || !prevMonthTotal) return null;
    const diff = monthStats.total - prevMonthTotal;
    const pct = prevMonthTotal ? (diff / prevMonthTotal) * 100 : 0;
    return { diff, pct, prev: prevMonthTotal };
  }, [compareMode, prevMonthTotal, monthStats.total]);

  const toggleCategory = (key) => {
    setActiveCategories(prev => {
      const all = categoriesInMonth.map(c => c.key);
      if (!prev || prev.size === 0) {
        // From "show all" → isolate clicked one
        return new Set([key]);
      }
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      // If user ended up with every category active, revert to "show all"
      if (next.size === 0 || next.size === all.length) return null;
      return next;
    });
  };

  return (
    <div className="space-y-4" data-testid="expenses-calendar">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => go(-1)} data-testid="cal-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="font-semibold min-w-[150px] text-center" data-testid="cal-month-label">
            {MONTH_NAMES_ES[month]} {year}
          </div>
          <Button variant="outline" size="sm" onClick={() => go(1)} data-testid="cal-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={compareMode ? "default" : "outline"}
            size="sm"
            onClick={() => setCompareMode(v => !v)}
            className={compareMode ? "bg-violet-600 hover:bg-violet-700" : ""}
            data-testid="cal-compare-toggle"
            title="Compara el gasto del mes con el mes anterior"
          >
            <BarChart2 className="h-3.5 w-3.5 mr-1" />
            Comparar mes anterior
          </Button>
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          {monthStats.days > 0 ? (
            <>Gasto del mes: <strong className="text-foreground">{fmtEUR(monthStats.total)}</strong> en {monthStats.days} día{monthStats.days === 1 ? "" : "s"}</>
          ) : (
            <>Sin gastos registrados en este mes</>
          )}
          {compareMode && (
            loadingPrev ? (
              <span className="text-[11px] text-muted-foreground">cargando mes anterior…</span>
            ) : delta ? (
              <span
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-semibold border ${
                  delta.pct > 2
                    ? "bg-red-50 text-red-700 border-red-200"
                    : delta.pct < -2
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-muted/50 text-muted-foreground border-border"
                }`}
                data-testid="cal-delta-badge"
                title={`Mes anterior: ${fmtEUR(delta.prev)}`}
              >
                {delta.pct > 2 ? <TrendingUp className="h-3 w-3" /> :
                 delta.pct < -2 ? <TrendingDown className="h-3 w-3" /> :
                 <Minus className="h-3 w-3" />}
                {delta.pct > 0 ? "+" : ""}{delta.pct.toFixed(0)}% vs {MONTH_NAMES_ES[(month + 11) % 12].slice(0, 3)}
              </span>
            ) : (
              <span className="text-[11px] text-muted-foreground">Sin datos del mes anterior</span>
            )
          )}
        </div>
      </div>

      {categoriesInMonth.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap pb-1" data-testid="cal-category-legend">
          <span className="text-[11px] text-muted-foreground uppercase tracking-wide mr-1">Filtrar:</span>
          {categoriesInMonth.map(c => {
            const dot = CATEGORY_DOT[c.key] || CATEGORY_DOT.other;
            const active = !activeCategories || activeCategories.size === 0 || activeCategories.has(c.key);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => toggleCategory(c.key)}
                data-testid={`cal-filter-${c.key}`}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] transition ${
                  active
                    ? "bg-card border-border text-foreground hover:border-violet-400"
                    : "bg-muted/50 border-border text-muted-foreground hover:bg-white"
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${dot} ${active ? "" : "opacity-40"}`} />
                <span className="font-medium">{catLabels?.[c.key] || c.key}</span>
                <span className="tabular-nums opacity-60">·&nbsp;{fmtEUR(c.sum)}</span>
              </button>
            );
          })}
          {activeCategories && activeCategories.size > 0 && (
            <button
              type="button"
              onClick={() => setActiveCategories(null)}
              className="text-[11px] text-violet-700 hover:text-violet-900 underline underline-offset-2 ml-2"
              data-testid="cal-filter-reset"
            >
              Mostrar todas
            </button>
          )}
        </div>
      )}

      {topSuppliers.length > 0 && (
        <div className="rounded-lg border bg-gradient-to-br from-violet-50 via-white to-white p-3" data-testid="cal-top-suppliers">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-violet-700/90">
              Top {topSuppliers.length} proveedores · {MONTH_NAMES_ES[month]}
            </span>
            {activeSupplier && (
              <button
                type="button"
                onClick={() => setActiveSupplier(null)}
                className="text-[11px] text-violet-700 hover:text-violet-900 underline underline-offset-2"
                data-testid="cal-supplier-reset"
              >
                Ver todos
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            {topSuppliers.map((s, idx) => {
              const dot = CATEGORY_DOT[(s.category || "other").toLowerCase()] || CATEGORY_DOT.other;
              const ratio = Math.max(0.02, s.sum / topMax);
              const isActive = activeSupplier === s.name;
              const isDimmed = activeSupplier && !isActive;
              return (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => setActiveSupplier(isActive ? null : s.name)}
                  data-testid={`cal-top-supplier-${idx}`}
                  className={`w-full group text-left relative flex items-center gap-2 rounded-md px-2 py-1.5 border transition ${
                    isActive
                      ? "border-violet-400 bg-violet-100 shadow-sm"
                      : isDimmed
                      ? "border-transparent bg-card/50 opacity-60 hover:opacity-100"
                      : "border-border bg-card hover:border-violet-300 hover:bg-violet-50/50"
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full shrink-0 ${dot}`} />
                  <span className="text-xs font-medium text-foreground w-5 shrink-0 tabular-nums text-muted-foreground">
                    {idx + 1}.
                  </span>
                  <span className="text-sm font-medium text-foreground truncate max-w-[220px] shrink-0">
                    {s.name}
                  </span>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {s.count} gasto{s.count === 1 ? "" : "s"}
                  </span>
                  <div className="flex-1 relative h-2 rounded-full bg-violet-100/70 overflow-hidden mx-1">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-violet-500 to-violet-600 rounded-full transition-[width]"
                      style={{ width: `${ratio * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-foreground tabular-nums shrink-0 min-w-[70px] text-right">
                    {fmtEURCents(s.sum)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Card className="overflow-hidden">
        <CardContent className="p-3">
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS_ES.map(w => (
              <div key={w} className="text-[11px] font-medium text-muted-foreground text-center py-1">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              const iso = toIso(d);
              const inMonth = d.getMonth() === month;
              const rows = filteredByDate.get(iso) || [];
              const sum = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
              const hasData = rows.length > 0;
              const isToday = iso === new Date().toISOString().slice(0, 10);
              const isSelected = iso === selected;
              const shade = shadeFor(sum);
              const darkShade = /text-white/.test(shade);
              const visible = rows.slice(0, 3);
              const hidden = rows.length - visible.length;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => hasData && setSelected(iso === selected ? null : iso)}
                  disabled={!hasData}
                  data-testid={`cal-day-${iso}`}
                  className={`relative min-h-[110px] rounded-md border transition p-1.5 flex flex-col items-stretch overflow-hidden text-left ${
                    inMonth ? "" : "opacity-30"
                  } ${shade} ${
                    isSelected ? "ring-2 ring-offset-1 ring-violet-600" : ""
                  } ${hasData ? "hover:shadow-sm hover:scale-[1.01] cursor-pointer" : "cursor-default"}`}
                  title={hasData ? `${rows.length} gasto${rows.length === 1 ? "" : "s"} · ${fmtEURCents(sum)}` : ""}
                >
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className={`text-xs font-semibold tabular-nums ${isToday ? "underline decoration-2 underline-offset-2" : ""}`}>
                      {d.getDate()}
                    </span>
                    {hasData && (
                      <span className={`text-[10px] font-bold tabular-nums ${darkShade ? "text-white/90" : "text-violet-900/80"}`}>
                        {fmtChipAmount(sum)}
                      </span>
                    )}
                  </div>
                  {hasData && (
                    <div className="flex flex-col gap-1 min-h-0">
                      {visible.map((r) => {
                        const cat = (r.category || "other").toLowerCase();
                        const dot = CATEGORY_DOT[cat] || CATEGORY_DOT.other;
                        const supplier = (r.supplier_name || catLabels?.[cat] || cat)
                          .replace(/\s+/g, " ")
                          .trim();
                        return (
                          <div
                            key={r.expense_id}
                            className={`flex items-center gap-1 rounded px-1 py-0.5 border text-[10px] leading-tight ${
                              darkShade
                                ? "bg-card/90 border-white/60 text-violet-900"
                                : "bg-card/85 border-violet-200/60 text-foreground"
                            }`}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${dot}`} />
                            <span className="flex-1 min-w-0 truncate font-medium">
                              {supplier}
                            </span>
                            <span className="tabular-nums shrink-0 text-[9px] font-semibold">
                              {fmtChipAmount(r.amount)}
                            </span>
                          </div>
                        );
                      })}
                      {hidden > 0 && (
                        <div className={`text-[10px] font-medium text-center py-0.5 rounded ${
                          darkShade ? "bg-card/20 text-white" : "bg-violet-200/40 text-violet-900"
                        }`}>
                          +{hidden} más
                        </div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {selected && selectedRows.length > 0 && (
        <Card className="border-violet-200" data-testid="cal-day-detail">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold">
                  {new Date(selected).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {selectedRows.length} gasto{selectedRows.length === 1 ? "" : "s"} · Total {fmtEURCents(selectedTotal)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-xs text-muted-foreground hover:text-gray-900"
              >
                Cerrar ✕
              </button>
            </div>
            <div className="space-y-1.5">
              {selectedRows.map(r => (
                <div
                  key={r.expense_id}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-50 border"
                  data-testid={`cal-row-${r.expense_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">
                        {r.supplier_name || catLabels?.[r.category] || r.category}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-foreground border border-border">
                        {catLabels?.[r.category] || r.category}
                      </span>
                      {r.source === "document" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200">
                          Desde doc
                        </span>
                      )}
                    </div>
                    {r.notes && (
                      <div className="text-[11px] text-muted-foreground truncate mt-0.5">{r.notes}</div>
                    )}
                  </div>
                  <div className="text-sm font-semibold shrink-0">{fmtEURCents(r.amount)}</div>
                  {r.source !== "document" && onDelete && (
                    <button
                      type="button"
                      onClick={() => onDelete(r)}
                      className="text-muted-foreground hover:text-red-700 p-1"
                      aria-label="Eliminar"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
